import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { ShieldCheck, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import walletApi from '../../lib/walletApi';

export default function AdminManagement() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', first_name: '', last_name: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try { setAdmins(await walletApi.get('/super-admin/admins').then(r => r.data)); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await walletApi.post('/super-admin/admins', form);
      toast.success('Admin created');
      setOpen(false);
      setForm({ email: '', password: '', first_name: '', last_name: '', phone: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id, email) => {
    if (!window.confirm(`Delete admin ${email}?`)) return;
    try { await walletApi.delete(`/super-admin/admins/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-pink-400" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-management">
      <div className="flex items-center justify-between">
        <div><h1 className="text-xl font-bold text-gray-900">Admin Management</h1><p className="text-sm text-gray-500">Manage super admin accounts</p></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-pink-500 hover:bg-pink-600 text-white" data-testid="create-admin-btn"><Plus className="w-4 h-4 mr-2" /> New Admin</Button></DialogTrigger>
          <DialogContent className="border-gray-200"><DialogHeader><DialogTitle>Create Super Admin</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-sm">First Name</Label><Input value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} required className="h-9" /></div>
                <div><Label className="text-sm">Last Name</Label><Input value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} required className="h-9" /></div>
              </div>
              <div><Label className="text-sm">Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="h-9" /></div>
              <div><Label className="text-sm">Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="h-9" /></div>
              <div><Label className="text-sm">Password</Label><Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required className="h-9" /></div>
              <Button type="submit" disabled={submitting} className="w-full bg-pink-500 hover:bg-pink-600 text-white">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Admin'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid gap-3">
        {admins.map(a => (
          <Card key={a.id} className="border-gray-200 shadow-sm">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-pink-50 text-pink-600 flex items-center justify-center text-sm font-bold">
                  {a.first_name?.[0]}{a.last_name?.[0]}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{a.first_name} {a.last_name}</p>
                  <p className="text-xs text-gray-500">{a.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-1 rounded-full bg-pink-50 text-pink-600 font-medium">Super Admin</span>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(a.id, a.email)} className="text-gray-400 hover:text-red-500" data-testid={`delete-admin-${a.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
